import {
  Button,
  Icon,
  PageHeader,
  Select,
  Table,
  TableColumn,
} from '@components';
import BudgetTemplateAPICaller from '@services/api/budgetTemplate';
import { usePermissionContext } from '@contexts/PermissionProvider';
import ConsolidatedBudgetAPICaller, {
  ConsolidatedBudgetFilters,
} from '@services/api/consolidatedBudget';
import { convertJsonToXls } from '@services/io/file';
import { BudgetTemplate } from 'interfaces/BudgetTemplate';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'utils/string/currency';
import { getPercentageClass } from 'utils/number';
import { groupOptions } from '@components/Input/Select';
import { useFilters } from '@hooks/useFilters';

const options = [
  { label: 'Receita', value: 'income' },
  { label: 'Despesa', value: 'expense' },
];

type BudgetConsolidatedResult = {
  costCentersWithTotalBudget: Array<{
    id: string;
    name: string;
    department: string;
    totalBudget: number;
    totalRealized: number;
    percentageUsage: number;
  }>;
  budgetTemplate: BudgetTemplate | null;
};

export default function ConsolidatedBudgetPage() {
  const navigate = useNavigate();

  const { hasPermission } = usePermissionContext();

  const [isLoading, setIsLoading] = useState(false);

  const [tableData, setTableData] = useState<{
    total: number;
    result: BudgetConsolidatedResult;
  }>({
    total: 0,
    result: {
      costCentersWithTotalBudget: [],
      budgetTemplate: null,
    },
  });

  const [budgetTemplatesFilter, setBudgetTemplatesFilter] = useState<
    Array<BudgetTemplate>
  >([]);

  const { filters, updateFilters: setFilters, clearFilters } = useFilters();

  useEffect(() => {
    clearFilters();
    setFilters({
      type: options[1].value,
      typeDescription: options[1].label,
      size: 20,
    });
  }, [clearFilters, setFilters]);

  const list = useCallback((filters: ConsolidatedBudgetFilters) => {
    if (!filters.budgetTemplateId || !filters.type) return;

    setIsLoading(true);
    ConsolidatedBudgetAPICaller.list(filters)
      .then((values) => setTableData(values))
      .finally(() => setIsLoading(false));
  }, []);

  const loadBudgetTemplatesFilter = useCallback(() => {
    if (hasPermission('budgetTemplates', 'read')) {
      BudgetTemplateAPICaller.list().then((values) => {
        setBudgetTemplatesFilter(values.result);
      });
    }
  }, [hasPermission]);

  const handleClickOnDonwloadXls = async () => {
    const response =
      await ConsolidatedBudgetAPICaller.listReportByBudgetTemplateAndType(
        filters
      );
    const { typeDescription, budgetTemplateName } = filters;
    const department = filters.department ? filters.department : 'Todos';
    const fileName = `Consolidado de ${typeDescription}s-${budgetTemplateName}-${department}.xlsx`;
    convertJsonToXls(response, fileName);
  };

  useEffect(() => {
    loadBudgetTemplatesFilter();
  }, [loadBudgetTemplatesFilter]);

  useEffect(() => {
    if (!filters.budgetTemplateId) {
      const budgetTemplate =
        budgetTemplatesFilter[budgetTemplatesFilter.length - 1];
      setFilters({
        budgetTemplateId: budgetTemplate?.id,
        budgetTemplateName: budgetTemplate?.name,
        budgetTemplate: budgetTemplate,
      });
      return;
    } else list(filters);
  }, [budgetTemplatesFilter, filters, setFilters, list]);

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Budget consolidado" />

        <div className="mt-s-200">
          <Table
            data={tableData.result.costCentersWithTotalBudget}
            dataKey="name"
            totalRecords={tableData.total}
            paginationLimit={20}
            withPagination
            withSearchInput
            withRedirectIcon
            className="pointer"
            showColumnSelector={false}
            isLoading={isLoading}
            filters={filters}
            setFilters={setFilters}
            onRowClick={(row) =>
              navigate(
                `/budget-consolidado/${filters.budgetTemplateId}/${row.id}`,
                { state: { type: filters.type } }
              )
            }
            additionalFilters={
              <div className="d-flex justify-end">
                <div className="col-sm-4 mr-s-200">
                  <Select
                    value={{
                      label: filters.budgetTemplate
                        ? `${filters.budgetTemplateName} - ${filters.budgetTemplate?.year}`
                        : '',
                      value: filters.budgetTemplateId,
                    }}
                    groupValue={'Budget - ' + filters.budgetTemplate?.year}
                    placeholder="Filtrar por modelo de budget"
                    options={groupOptions(budgetTemplatesFilter, {
                      optionLabel: 'name',
                      optionValue: 'id',
                      groupLabel: 'year',
                      groupPrefix: 'Budget',
                    })}
                    onSelect={(budgetTemplate: any) => {
                      setFilters({
                        budgetTemplateId: budgetTemplate.value,
                        budgetTemplateName: budgetTemplate.label,
                        budgetTemplate: budgetTemplatesFilter.find(
                          (item) => item.id === budgetTemplate.value
                        ),
                      });
                    }}
                  />
                </div>

                <div className="col-sm-4">
                  <Select
                    value={{
                      value: filters.type,
                      label: filters.typeDescription,
                    }}
                    placeholder="Filtrar por Tipo de Budget"
                    options={options}
                    defaultValue={options[1]}
                    onSelect={(type: any) => {
                      setFilters({
                        type: type.value,
                        typeDescription: type.label,
                      });
                    }}
                  />
                </div>
                <div>
                  <Button
                    className="ml-s-200"
                    suffixes={<Icon>download</Icon>}
                    design="outlined"
                    type="submit"
                    onClick={handleClickOnDonwloadXls}
                  >
                    Exportar XLS
                  </Button>
                </div>
              </div>
            }
          >
            <TableColumn fromKey="name" header="Centro de custo" />
            <TableColumn
              fromKey="totalBudget"
              header={filters.budgetTemplateName}
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
            <TableColumn
              fromKey="totalRealized"
              header="Realizado"
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
            <TableColumn
              fromKey="percentageUsage"
              width="10%"
              header="Porcentagem"
              headerAlign="center"
              textAlign="right"
              render={(percentage) => {
                return (
                  <div className={getPercentageClass(percentage)}>
                    {Number(percentage).toFixed(2)}%
                  </div>
                );
              }}
            />
          </Table>
        </div>
      </div>
    </>
  );
}
